import { queryOptions } from "@tanstack/react-query";

import { apiUrl } from "@workspace/base-api/apiUrl";
import type { HttpRequestError } from "@workspace/base-api/fetchJson";
import {
	appFetch,
	ensureOk,
	fetchJson,
	toApiError,
} from "@workspace/base-api/fetchJson";
import { toCamel } from "@workspace/shared";

import type {
	ArchiveStaffMemberResult,
	RecordEarlyLeaveInput,
	Shift,
	StaffArchiveImpact,
	StaffingData,
	StaffMember,
	StaffMetrics,
} from "./types";

export const venueStaffManagementApi = {
	async getDashboard(
		tenant: string,
		venueId: string,
		section: string = "",
		start: Date | null = null,
		end: Date | null = null,
	) {
		const s = section.length > 0 ? `/${section}` : "";
		const url = new URL(`/venue-management/${venueId}/dashboard${s}`, apiUrl);

		const body: Record<string, string> = {};
		if (start) {
			body.start = start.toISOString();
		}
		if (end) {
			body.end = end.toISOString();
		}

		return await fetchJson<{
			data: {
				staff: StaffingData | null | undefined;
			};
		}>(url.toString(), {
			method: "POST",
			headers: {
				tenant: tenant || "",
			},
			body: JSON.stringify(body),
		});
	},
	getDashboardOptions: (
		tenant: string,
		venueId: string,
		section: string = "",
		start: Date | null = null,
		end: Date | null = null,
	) => {
		return queryOptions({
			queryKey: [
				tenant,
				"venue-management",
				"dashboard",
				venueId,
				section,
				start,
				end,
			],
			queryFn: () =>
				venueStaffManagementApi.getDashboard(
					tenant,
					venueId,
					section,
					start,
					end,
				),
		});
	},
	async postVenueRole(
		tenant: string,
		venueId: string,
		data: Record<string, unknown>,
	) {
		const url = new URL(`/venue-management/${venueId}/venue-roles`, apiUrl);
		try {
			const response = await ensureOk(
				await appFetch(url.toString(), {
					headers: {
						tenant: tenant || "",
					},
					method: "POST",
					body: JSON.stringify(data),
				}),
			);

			return {
				success: true as const,
				response,
			};
		} catch (e: unknown) {
			return await toApiError(e, "Failed to save role");
		}
	},
	postVenueRoleOptions: (tenant: string, venueId: string) => {
		return {
			mutationKey: [tenant, "venue-management", venueId, "venue-roles", "add"],
			mutationFn: (data: Record<string, unknown>) =>
				venueStaffManagementApi.postVenueRole(tenant, venueId, data),
		};
	},
	async deleteVenueRole(tenant: string, venueId: string, roleId: string) {
		const url = new URL(
			`/venue-management/${venueId}/venue-roles/${roleId}`,
			apiUrl,
		);
		try {
			const response = await ensureOk(
				await appFetch(url.toString(), {
					headers: {
						tenant: tenant || "",
					},
					method: "DELETE",
				}),
			);

			return {
				success: true as const,
				response,
			};
		} catch (e: unknown) {
			return await toApiError(e, "Failed to delete role");
		}
	},
	deleteVenueRoleOptions: (tenant: string, venueId: string) => {
		return {
			mutationKey: [
				tenant,
				"venue-management",
				venueId,
				"venue-roles",
				"delete",
			],
			mutationFn: (roleId: string) =>
				venueStaffManagementApi.deleteVenueRole(tenant, venueId, roleId),
		};
	},
	async addStaffMember(
		tenant: string,
		venueId: string,
		data: Record<string, unknown>,
	) {
		const url = new URL(`/venue-management/${venueId}/staff`, apiUrl);

		try {
			const response = await fetchJson<{ id: string }>(url.toString(), {
				headers: {
					tenant: tenant || "",
				},
				method: "POST",
				body: JSON.stringify(data),
			});

			return {
				success: true,
				response,
			};
		} catch (e: unknown) {
			const error = e as HttpRequestError;
			return {
				success: false,
				details: toCamel(await error.response.json()),
				error: error.message,
			};
		}
	},
	addStaffMemberOptions: (tenant: string, venueId: string) => {
		return {
			mutationKey: [tenant, "venue-management", venueId, "staff", "add"],
			mutationFn: (data: Record<string, unknown>) =>
				venueStaffManagementApi.addStaffMember(tenant, venueId, data),
		};
	},
	async updateStaffMember(
		tenant: string,
		venueId: string,
		id: string,
		data: Record<string, unknown>,
	) {
		const url = new URL(`/venue-management/${venueId}/staff/${id}`, apiUrl);

		try {
			const response = await fetchJson<{ id: string }>(url.toString(), {
				headers: {
					tenant: tenant || "",
				},
				method: "PUT",
				body: JSON.stringify(data),
			});

			return {
				success: true,
				response,
			};
		} catch (e: unknown) {
			const error = e as HttpRequestError;
			return {
				success: false,
				details: toCamel(await error.response.json()),
				error: error.message,
			};
		}
	},
	updateStaffMemberOptions: (tenant: string, venueId: string) => {
		return {
			mutationKey: [tenant, "venue-management", venueId, "staff", "update"],
			mutationFn: ({
				id,
				data,
			}: {
				id: string;
				data: Record<string, unknown>;
			}) =>
				venueStaffManagementApi.updateStaffMember(tenant, venueId, id, data),
		};
	},
	async resetPin(
		tenant: string,
		venueId: string,
		staffMemberId: string,
		data: Record<string, unknown> = {},
	) {
		const url = new URL(
			`/venue-management/${venueId}/staff/${staffMemberId}/pin/reset`,
			apiUrl,
		);

		try {
			const response = await fetchJson<{ pin: string }>(url.toString(), {
				headers: {
					tenant: tenant || "",
				},
				method: "PUT",
				body: JSON.stringify(data),
			});

			return {
				success: true,
				response,
			};
		} catch (e: unknown) {
			const error = e as HttpRequestError;
			return {
				success: false,
				details: toCamel(await error.response.json()),
				error: error.message,
			};
		}
	},
	resetPinOptions: (tenant: string, venueId: string) => {
		return {
			mutationKey: [tenant, "venue-management", venueId, "staff", "reset-pin"],
			mutationFn: (data: { staffMemberId: string }) =>
				venueStaffManagementApi.resetPin(
					tenant,
					venueId,
					data.staffMemberId,
					data,
				),
		};
	},
	// HA-283 — archive/restore are per-venue, so both carry the venue in the path. The 409s
	// (clocked in, open orders) come back as ProblemDetails, which the caller renders from
	// `details.detail` rather than the generic message.
	async archiveStaffMember(
		tenant: string,
		venueId: string,
		staffMemberId: string,
	) {
		const url = new URL(
			`/venue-management/${venueId}/staff/${staffMemberId}/archive`,
			apiUrl,
		);

		try {
			const response = await fetchJson<ArchiveStaffMemberResult>(
				url.toString(),
				{
					headers: {
						tenant: tenant || "",
					},
					method: "POST",
					body: JSON.stringify({}),
				},
			);

			return {
				success: true,
				response,
			};
		} catch (e: unknown) {
			const error = e as HttpRequestError;
			return {
				success: false,
				details: toCamel(await error.response.json()),
				error: error.message,
			};
		}
	},
	archiveStaffMemberOptions: (tenant: string, venueId: string) => {
		return {
			mutationKey: [tenant, "venue-management", venueId, "staff", "archive"],
			mutationFn: (staffMemberId: string) =>
				venueStaffManagementApi.archiveStaffMember(
					tenant,
					venueId,
					staffMemberId,
				),
		};
	},
	// HA-1026 — the shifts an archive would leave uncovered, listed in the Archive confirmation before the user confirms.
	async getArchiveImpact(
		tenant: string,
		venueId: string,
		staffMemberId: string,
	) {
		const url = new URL(
			`/venue-management/${venueId}/staff/${staffMemberId}/archive-impact`,
			apiUrl,
		);

		return await fetchJson<StaffArchiveImpact>(url.toString(), {
			method: "GET",
			headers: {
				tenant: tenant || "",
			},
		});
	},
	archiveImpactOptions: (
		tenant: string,
		venueId: string,
		staffMemberId: string,
	) => {
		return queryOptions({
			queryKey: [
				tenant,
				"venue-management",
				venueId,
				"staff",
				staffMemberId,
				"archive-impact",
			],
			queryFn: () =>
				venueStaffManagementApi.getArchiveImpact(
					tenant,
					venueId,
					staffMemberId,
				),
		});
	},
	async restoreStaffMember(
		tenant: string,
		venueId: string,
		staffMemberId: string,
	) {
		const url = new URL(
			`/venue-management/${venueId}/staff/${staffMemberId}/restore`,
			apiUrl,
		);

		try {
			const response = await fetchJson<{ staffMemberId: string }>(
				url.toString(),
				{
					headers: {
						tenant: tenant || "",
					},
					method: "POST",
					body: JSON.stringify({}),
				},
			);

			return {
				success: true,
				response,
			};
		} catch (e: unknown) {
			const error = e as HttpRequestError;
			return {
				success: false,
				details: toCamel(await error.response.json()),
				error: error.message,
			};
		}
	},
	restoreStaffMemberOptions: (tenant: string, venueId: string) => {
		return {
			mutationKey: [tenant, "venue-management", venueId, "staff", "restore"],
			mutationFn: (staffMemberId: string) =>
				venueStaffManagementApi.restoreStaffMember(
					tenant,
					venueId,
					staffMemberId,
				),
		};
	},
	async addContract(
		tenant: string,
		venueId: string,
		data: Record<string, unknown>,
	) {
		const url = new URL(`/venue-management/${venueId}/staff/contracts`, apiUrl);

		try {
			const response = await fetchJson<{ id: string }>(url.toString(), {
				headers: {
					tenant: tenant || "",
				},
				method: "POST",
				body: JSON.stringify(data),
			});

			return {
				success: true,
				response,
			};
		} catch (e: unknown) {
			const error = e as HttpRequestError;
			return {
				success: false,
				details: toCamel(await error.response.json()),
				error: error.message,
			};
		}
	},
	addContractOptions: (tenant: string, venueId: string) => {
		return {
			mutationKey: [tenant, "venue-management", venueId, "contracts", "add"],
			mutationFn: (data: Record<string, unknown>) =>
				venueStaffManagementApi.addContract(tenant, venueId, data),
		};
	},
	async updateContract(
		tenant: string,
		venueId: string,
		id: string,
		data: Record<string, unknown>,
	) {
		const url = new URL(
			`/venue-management/${venueId}/staff/contracts/${id}`,
			apiUrl,
		);

		try {
			const response = await fetchJson<{ id: string }>(url.toString(), {
				headers: {
					tenant: tenant || "",
				},
				method: "PUT",
				body: JSON.stringify(data),
			});

			return {
				success: true,
				response,
			};
		} catch (e: unknown) {
			const error = e as HttpRequestError;
			return {
				success: false,
				details: toCamel(await error.response.json()),
				error: error.message,
			};
		}
	},
	updateContractOptions: (tenant: string, venueId: string, id: string) => {
		return {
			mutationKey: [
				tenant,
				"venue-management",
				venueId,
				"contracts",
				"update",
				id,
			],
			mutationFn: (data: Record<string, unknown>) =>
				venueStaffManagementApi.updateContract(tenant, venueId, id, data),
		};
	},
	async addShift(
		tenant: string,
		venueId: string,
		data: Record<string, unknown>,
	) {
		const url = new URL(`/venue-management/${venueId}/staff/shifts`, apiUrl);

		try {
			const response = await fetchJson<{ id: string }>(url.toString(), {
				headers: {
					tenant: tenant || "",
				},
				method: "POST",
				body: JSON.stringify(data),
			});

			return {
				success: true,
				response,
			};
		} catch (e: unknown) {
			const error = e as HttpRequestError;
			return {
				success: false,
				details: toCamel(await error.response.json()),
				error: error.message,
			};
		}
	},
	addShiftOptions: (tenant: string, venueId: string) => {
		return {
			mutationKey: [tenant, "venue-management", venueId, "shifts", "add"],
			mutationFn: (data: Record<string, unknown>) =>
				venueStaffManagementApi.addShift(tenant, venueId, data),
		};
	},
	async updateShift(
		tenant: string,
		venueId: string,
		id: string,
		data: Record<string, unknown>,
	) {
		const url = new URL(
			`/venue-management/${venueId}/staff/shifts/${id}`,
			apiUrl,
		);

		try {
			const response = await fetchJson<{ id: string }>(url.toString(), {
				headers: {
					tenant: tenant || "",
				},
				method: "PUT",
				body: JSON.stringify(data),
			});

			return {
				success: true,
				response,
			};
		} catch (e: unknown) {
			const error = e as HttpRequestError;
			return {
				success: false,
				details: toCamel(await error.response.json()),
				error: error.message,
			};
		}
	},
	updateShiftOptions: (tenant: string, venueId: string) => {
		return {
			mutationKey: [tenant, "venue-management", venueId, "shifts", "update"],
			mutationFn: ({
				id,
				data,
			}: {
				id: string;
				data: Record<string, unknown>;
			}) => venueStaffManagementApi.updateShift(tenant, venueId, id, data),
		};
	},
	async setShiftStatus(
		tenant: string,
		venueId: string,
		shiftId: string,
		data: Record<string, unknown>,
	) {
		const url = new URL(
			`/venue-management/${venueId}/staff/shifts/${shiftId}/status`,
			apiUrl,
		);

		try {
			const response = await fetchJson<{ id: string }>(url.toString(), {
				headers: {
					tenant: tenant || "",
				},
				method: "PUT",
				body: JSON.stringify(data),
			});

			return {
				success: true,
				response,
			};
		} catch (e: unknown) {
			const error = e as HttpRequestError;
			return {
				success: false,
				details: toCamel(await error.response.json()),
				error: error.message,
			};
		}
	},
	setShiftStatusOptions: (tenant: string, venueId: string, shiftId: string) => {
		return {
			mutationKey: [
				tenant,
				"venue-management",
				venueId,
				"shifts",
				"status",
				shiftId,
			],
			mutationFn: (data: Record<string, unknown>) =>
				venueStaffManagementApi.setShiftStatus(tenant, venueId, shiftId, data),
		};
	},
	async setUserPin(
		tenant: string,
		venueId: string,
		userId: string,
		data: Record<string, unknown>,
	) {
		const url = new URL(
			`/venue-management/${venueId}/staff/${userId}/pin`,
			apiUrl,
		);

		try {
			const response = await fetchJson<{ id: string }>(url.toString(), {
				headers: {
					tenant: tenant || "",
				},
				method: "PUT",
				body: JSON.stringify(data),
			});

			return {
				success: true,
				response,
			};
		} catch (e: unknown) {
			const error = e as HttpRequestError;
			return {
				success: false,
				details: toCamel(await error.response.json()),
				error: error.message,
			};
		}
	},
	setUserPinOptions: (tenant: string, venueId: string, userId: string) => {
		return {
			mutationKey: [
				tenant,
				"venue-management",
				venueId,
				"staff",
				"pin",
				userId,
			],
			mutationFn: (data: Record<string, unknown>) =>
				venueStaffManagementApi.setUserPin(tenant, venueId, userId, data),
		};
	},
	async syncScreensWithExternal(
		tenant: string,
		venueId: string,
		data: Record<string, unknown>,
	) {
		const url = new URL(`/venue-management/${venueId}/screens/sync`, apiUrl);

		try {
			const response = await fetchJson<{ id: string }>(url.toString(), {
				headers: {
					tenant: tenant || "",
				},
				method: "POST",
				body: JSON.stringify(data),
			});

			return {
				success: true,
				response,
			};
		} catch (e: unknown) {
			const error = e as HttpRequestError;
			return {
				success: false,
				details: toCamel(await error.response.json()),
				error: error.message,
			};
		}
	},
	syncScreensWithExternalOptions: (tenant: string, venueId: string) => {
		return {
			mutationKey: [tenant, "venue-management", venueId, "screens", "sync"],
			mutationFn: (data: Record<string, unknown>) =>
				venueStaffManagementApi.syncScreensWithExternal(tenant, venueId, data),
		};
	},
	async addMedia(
		tenant: string,
		venueId: string,
		data: Record<string, unknown>,
	) {
		const url = new URL(`/venue-management/${venueId}/screens/media`, apiUrl);

		try {
			const response = await fetchJson<{ id: string }>(url.toString(), {
				headers: {
					tenant: tenant || "",
				},
				method: "POST",
				body: JSON.stringify(data),
			});

			return {
				success: true,
				response,
			};
		} catch (e: unknown) {
			const error = e as HttpRequestError;
			return {
				success: false,
				details: toCamel(await error.response.json()),
				error: error.message,
			};
		}
	},
	addMediaOptions: (tenant: string, venueId: string) => {
		return {
			mutationKey: [tenant, "venue-management", venueId, "media", "add"],
			mutationFn: (data: Record<string, unknown>) =>
				venueStaffManagementApi.addMedia(tenant, venueId, data),
		};
	},
	async getStaffMetrics(
		tenant: string,
		venueId: string,
		staffId: string,
		timeFilter: string,
	) {
		const url = new URL(
			`/venue-management/${venueId}/staff/${staffId}/metrics`,
			apiUrl,
		);
		url.searchParams.append("periodType", timeFilter);

		return await fetchJson<StaffMetrics>(url.toString(), {
			method: "GET",
			headers: {
				tenant: tenant || "",
			},
		});
	},
	getStaffMetricsOptions: (
		tenant: string,
		venueId: string,
		staffId: string,
		timeFilter: string,
	) => {
		return queryOptions({
			queryKey: [
				tenant,
				"venue-management",
				venueId,
				"staff",
				staffId,
				"metrics",
				timeFilter,
			],
			queryFn: () =>
				venueStaffManagementApi.getStaffMetrics(
					tenant,
					venueId,
					staffId,
					timeFilter,
				),
		});
	},
};

/** How often the kiosk data is re-read while the picker has nobody to draw. HA-663. */
export const KIOSK_EMPTY_REFETCH_MS = 3_000;

/**
 * HA-663 — poll the kiosk data only while no one is checked in.
 *
 * <para><b>Why a poll is needed at all.</b> A check-in commits and returns 200, but the picker
 * reads <c>VenueDashboardStaffData</c>, produced by an Async projection. Open EPOS in the gap and
 * the picker has nothing to draw for a check-in that has already happened. Keeping the projection
 * Async is the design; noticing when it lands is this.</para>
 *
 * <para><b>And only while empty.</b> Once somebody is checked in the answer stops changing in a way
 * the picker cares about — it is replaced by the order screen — so a till open all service would
 * otherwise re-read staff every few seconds for the rest of the day for nothing.</para>
 *
 * <para>Note the kiosk key is ALREADY refetched every 10s by the terminal's own kiosk observer
 * (`use-kiosk-overview.ts`), which shares this exact query key. This is deliberately shorter and
 * conditional: it closes the gap faster while the screen is unusable, and switches itself off
 * rather than adding a second permanent poller alongside that one.</para>
 */
export function kioskRefetchInterval(
	data: { shifts?: { status: string }[] } | undefined,
): number | false {
	if (data === undefined) return KIOSK_EMPTY_REFETCH_MS;

	const anyCheckedIn = (data.shifts ?? []).some(
		(s) => s.status === "CheckedIn",
	);

	return anyCheckedIn ? false : KIOSK_EMPTY_REFETCH_MS;
}

export const staffPortalApi = {
	async get(tenant: string, venueId: string) {
		const url = new URL(`/venue-management/${venueId}/kiosk`, apiUrl);

		return await fetchJson<{
			staffMembers: StaffMember[];
			shifts: Shift[];
		}>(url.toString(), {
			method: "GET",
			headers: { tenant: tenant || "" },
		});
	},

	getOptions: (tenant: string, venueId: string) =>
		queryOptions({
			queryKey: [tenant, venueId, "kiosk"],
			queryFn: () => staffPortalApi.get(tenant, venueId),
			refetchInterval: (query) => kioskRefetchInterval(query.state.data),
		}),

	async checkIn(
		tenant: string,
		venueId: string,
		data: Record<string, unknown>,
	) {
		const url = new URL(`/venue-management/${venueId}/kiosk/check-in`, apiUrl);

		try {
			await fetchJson(url.toString(), {
				headers: {
					tenant: tenant || "",
				},
				method: "POST",
				body: JSON.stringify(data),
			});

			return {
				success: true,
			};
		} catch (e: unknown) {
			const error = e as HttpRequestError;
			return {
				success: false,
				details: toCamel(await error.response.json()),
				error: error.message,
			};
		}
	},
	// HA-368: ending a checked-in shift early, with its reason (and a note with Other). Replaced kiosk/check-out.
	async recordEarlyLeave(
		tenant: string,
		venueId: string,
		data: RecordEarlyLeaveInput,
	) {
		const url = new URL(
			`/venue-management/${venueId}/kiosk/early-leave`,
			apiUrl,
		);

		try {
			await fetchJson(url.toString(), {
				headers: {
					tenant: tenant || "",
				},
				method: "POST",
				body: JSON.stringify(data),
			});

			return {
				success: true as const,
			};
		} catch (e: unknown) {
			// HA-782: a network drop has no response to read; toApiError (HA-769) names it instead
			// of throwing, which the check-in screen used to report as a wrong PIN.
			return await toApiError(e, "The early leave was not recorded");
		}
	},
	recordEarlyLeaveOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, "venue-management", venueId, "kiosk", "early-leave"],
		mutationFn: (data: RecordEarlyLeaveInput) =>
			staffPortalApi.recordEarlyLeave(tenant, venueId, data),
	}),
	async checkInOut(
		tenant: string,
		venueId: string,
		data: Record<string, unknown>,
	) {
		const url = new URL(
			`/venue-management/${venueId}/kiosk/check-in-out`,
			apiUrl,
		);

		// HA-829: the till's own session makes the call; the PIN's single-use ticket rides in the body
		// (`data.ticket`) and says who.
		try {
			const response = await fetchJson<StaffingData>(url.toString(), {
				headers: {
					tenant: tenant || "",
				},
				method: "POST",
				body: JSON.stringify(data),
			});

			return {
				success: true as const,
				response,
			};
		} catch (e: unknown) {
			// HA-782: a network drop has no response to read; toApiError (HA-769) names it instead
			// of throwing, which the check-in screen used to report as a wrong PIN.
			return await toApiError(e, "The check-in or check-out was not recorded");
		}
	},
	checkInOutOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, "venue-management", venueId, "kiosk", "check-in-out"],
		mutationFn: (data: Record<string, unknown>) =>
			staffPortalApi.checkInOut(tenant, venueId, data),
	}),
	async checkPin(
		tenant: string,
		venueId: string,
		data: Record<string, unknown>,
	) {
		const url = new URL(
			`/venue-management/${venueId}/kiosk/pin-authentication`,
			apiUrl,
		);

		try {
			// HA-829: a PIN proves one kiosk action: a two-minute, single-use ticket, never a token.
			const response = await fetchJson<{
				ticket: string;
				registeredUserId: string;
				staffMemberId: string;
				isTemporary: boolean;
				expiresAt: string;
			}>(url.toString(), {
				headers: {
					tenant: tenant || "",
				},
				method: "POST",
				body: JSON.stringify(data),
			});

			return {
				success: true as const,
				data: response,
			};
		} catch (e: unknown) {
			// HA-782: a network drop has no response to read; toApiError (HA-769) names it instead
			// of throwing, which the check-in screen used to report as a wrong PIN.
			return await toApiError(e, "The PIN could not be checked");
		}
	},
	checkPinOptions: (tenant: string, venueId: string) => {
		return {
			mutationKey: [
				tenant,
				"venue-management",
				venueId,
				"kiosk",
				"pin-authentication",
			],
			mutationFn: (data: Record<string, unknown>) =>
				staffPortalApi.checkPin(tenant, venueId, data),
		};
	},
	async resetPin(
		tenant: string,
		venueId: string,
		data: { staffMemberId: string } & Record<string, unknown>,
	) {
		const url = new URL(
			`/venue-management/${venueId}/staff/${data.staffMemberId}/pin/reset`,
			apiUrl,
		);

		try {
			const response = await fetchJson(url.toString(), {
				headers: {
					tenant: tenant || "",
					Authorization: `Bearer ${data.token}`,
				},
				method: "POST",
				body: JSON.stringify(data),
			});

			return {
				success: true,
				data: response,
			};
		} catch (e: unknown) {
			const error = e as HttpRequestError;
			return {
				success: false,
				details: toCamel(await error.response.json()),
				error: error.message,
			};
		}
	},
	resetPinOptions: (tenant: string, venueId: string) => {
		return {
			mutationKey: [tenant, "venue-management", venueId, "kiosk", "pin-reset"],
			mutationFn: (data: { staffMemberId: string } & Record<string, unknown>) =>
				staffPortalApi.resetPin(tenant, venueId, data),
		};
	},
	// HA-829: the terminal's own set-PIN route. The PIN's single-use ticket says whose PIN it is; the
	// till's session makes the call (the dashboard's route would judge the till's user).
	async setPin(
		tenant: string,
		venueId: string,
		data: { staffMemberId: string; ticket: string; pin: string },
	) {
		const url = new URL(`/venue-management/${venueId}/kiosk/pin`, apiUrl);

		try {
			await fetchJson(url.toString(), {
				headers: {
					tenant: tenant || "",
				},
				method: "PUT",
				body: JSON.stringify(data),
			});

			return {
				success: true as const,
			};
		} catch (e: unknown) {
			return await toApiError(e, "The PIN was not changed");
		}
	},
	setPinOptions: (tenant: string, venueId: string) => {
		return {
			mutationKey: [tenant, "venue-management", venueId, "kiosk", "pin-set"],
			mutationFn: (data: {
				staffMemberId: string;
				ticket: string;
				pin: string;
			}) => staffPortalApi.setPin(tenant, venueId, data),
		};
	},
	// HA-649 Slice 0: the Employee Centre sign-in, proved by the staff member's PIN (the ticket checkPin returned). It starts a
	// session whose token is good on the Employee Centre's routes at this venue only, for 15 minutes. A role without the
	// Employee Centre key is refused (403 EMPLOYEE_CENTRE_NOT_ALLOWED).
	async startEmployeeCentreSession(
		tenant: string,
		venueId: string,
		data: { staffMemberId: string; ticket: string },
	) {
		const url = new URL(
			`/venue-management/${venueId}/kiosk/employee-centre-session`,
			apiUrl,
		);

		return await fetchJson<{
			token: string;
			sessionId: string;
			registeredUserId: string;
			name: string;
			expires: string;
		}>(url.toString(), {
			method: "POST",
			headers: { tenant: tenant || "" },
			body: JSON.stringify(data),
		});
	},
	// HA-831: the EPOS staff switch, proved by the staff member's PIN (the ticket checkPin returned).
	// It starts a session whose token is good on the EPOS routes at this venue only.
	async startStaffSession(
		tenant: string,
		venueId: string,
		data: { staffMemberId: string; ticket: string },
	) {
		const url = new URL(
			`/venue-management/${venueId}/kiosk/staff-session`,
			apiUrl,
		);

		return await fetchJson<{
			token: string;
			sessionId: string;
			registeredUserId: string;
			name: string;
			expires: string;
		}>(url.toString(), {
			method: "POST",
			headers: { tenant: tenant || "" },
			body: JSON.stringify(data),
		});
	},
};
